import type { InvoiceDisplayStatus } from './invoice';
import type { PublicRepairCompanyContact } from './public';
import type { TaskAttachment, TaskSchedulingFields } from './task';

export type ContactType = 'RESIDENTIAL' | 'COMMERCIAL' | 'PROPERTY_MANAGER';

export type ContactSource = 'MANUAL' | 'LEAD' | 'REFERRAL' | 'WEBSITE' | 'IMPORT';

export type LeadSource =
  | 'ANGI'
  | 'THUMBTACK'
  | 'YELP'
  | 'GOOGLE'
  | 'FACEBOOK'
  | 'REFERRAL'
  | 'WEBSITE'
  | 'DIRECT'
  | 'OTHER';

export type PreferredChannel = 'SMS' | 'EMAIL' | 'PHONE' | 'ALL';

export type PhoneLabel = 'MOBILE' | 'HOME' | 'WORK' | 'FAX' | 'OTHER';

export type EmailLabel = 'PERSONAL' | 'WORK' | 'BILLING' | 'OTHER';

export type AccountingSyncStatus = 'PENDING' | 'SYNCED' | 'FAILED' | 'NOT_APPLICABLE';

export type AccountingSyncProvider = 'QBO' | 'XERO' | 'NETSUITE';

/**
 * Reasons a customer can be tax-exempt. Mirrors the backend `TaxExemptReason`
 * enum (apps/operations .../contacts/interfaces/contact.interfaces.ts) so the
 * value sent to `PUT /contacts/:id/tax-exemption` round-trips unchanged.
 */
export const TAX_EXEMPT_REASONS = ['GOVERNMENT', 'NONPROFIT', 'RESALE', 'TRIBAL', 'OTHER'] as const;

export type TaxExemptReason = (typeof TAX_EXEMPT_REASONS)[number];

/** Human-readable labels for the customer-level tax-exemption reason dropdown. */
export const TAX_EXEMPT_REASON_LABELS: Record<TaxExemptReason, string> = {
  GOVERNMENT: 'Government',
  NONPROFIT: 'Nonprofit',
  RESALE: 'Resale',
  TRIBAL: 'Tribal',
  OTHER: 'Other',
};

export type PaymentMethodProvider = 'STRIPE' | 'PAYABLI';

export type PaymentMethodType = 'CREDIT_CARD' | 'DEBIT_CARD' | 'ACH' | 'BANK_ACCOUNT';

export interface ContactAddress {
  id?: string;
  title?: string | null;
  label?: string | null;
  street1?: string | null;
  street?: string;
  street2?: string;
  city?: string;
  state?: string;
  postalCode?: string | null;
  zip?: string;
  country?: string;
  isBilling?: boolean;
  isService?: boolean;
  isPrimary?: boolean;
  accessNotes?: string;
  territory?: string;
  taxJurisdictionId?: string | null;
  taxJurisdiction?: string;
  isTaxExempt?: boolean;
  taxExemptCertificate?: string | null;
  taxExemptExpiry?: string | null;
  customFields?: { fieldId?: string; value: unknown }[];
  lat?: number | null;
  lng?: number | null;
  timezone?: string | null;
  timezoneSource?: string | null;
  timezoneResolvedAt?: string | null;
}

export interface ContactPhoneNumber {
  number: string;
  label?: PhoneLabel | null;
  isPrimary?: boolean;
}

export interface ContactEmail {
  email: string;
  label?: EmailLabel | null;
  isPrimary?: boolean;
}

export interface CommunicationPreferences {
  preferredChannel?: PreferredChannel;
  preferredTime?: string;
  language?: string;
  doNotCall?: boolean;
  doNotEmail?: boolean;
}

/**
 * Wave 2 §6.4 / §7.3 — Payment-method health classification surfaced to
 * the UI. HARD methods are excluded from auto-charge retries (§7.5) and
 * from default-promotion. String literal mirrors the Prisma enum value
 * so the JSON wire format round-trips unchanged.
 */
export type PaymentMethodDeclineState = 'HEALTHY' | 'SOFT_DECLINED' | 'HARD_DECLINED' | 'INVALID';

export interface ContactPaymentMethod {
  id: string;
  provider: PaymentMethodProvider | string;
  providerPaymentMethodId: string;
  providerCustomerId?: string | null;
  type: PaymentMethodType | string;
  brand?: string | null;
  last4?: string | null;
  expMonth?: number | null;
  expYear?: number | null;
  label?: string | null;
  cardholderName?: string | null;
  isDefault: boolean;
  isExpired: boolean;
  addedAt?: string | null;
  addedBy?: string | null;
  // ── Wave 2 — Health tracking (spec §6.4 / §7.3 / §7.5) ────────────────
  declineState?: PaymentMethodDeclineState;
  lastDeclineAt?: string | null;
  lastDeclineCode?: string | null;
  lastUsedAt?: string | null;
}

export type ContactPaymentRequestChannel = 'sms' | 'email' | 'both';

/** Explicit rail for the staff saved-payment-method SetupIntent. */
export type ContactPaymentMethodSetupRail = 'card' | 'ach';

export type ContactCommunicationChannel = 'SMS' | 'EMAIL' | 'PHONE' | 'IN_APP';

export interface ContactPaymentMethodSetupIntent {
  provider: PaymentMethodProvider | string;
  clientSecret: string;
  providerCustomerId: string;
  // Publishable key + connected-account id for the client-side
  // `loadStripe(publishableKey, { stripeAccount })`. Required for Stripe
  // Connect tenants; falls back to the platform publishable key.
  publishableKey: string;
  stripeAccountId: string | null;
}

/**
 * The setup-intent as the UNAUTHENTICATED add-card page receives it.
 *
 * Same three fields Stripe Elements needs, minus `providerCustomerId`: the
 * backend resolves the contact's own Stripe customer server-side on completion
 * rather than trusting one echoed back by an anonymous browser. The staff
 * route still returns it, so this is a separate type rather than a change to
 * `ContactPaymentMethodSetupIntent`.
 */
export interface PublicContactPaymentMethodSetupIntent {
  provider: PaymentMethodProvider | string;
  clientSecret: string;
  publishableKey: string;
  stripeAccountId: string | null;
}

/**
 * Body of the UNAUTHENTICATED add-card completion. Narrower than
 * `CreateContactPaymentMethodDto` on purpose: these two fields are everything
 * this route needs, and the backend's global `forbidNonWhitelisted` pipe
 * rejects the staff DTO's other properties (`label`, `makeDefault`, `type`,
 * `brand`, `last4`, …) with a 400.
 *
 * Do not add `providerCustomerId` back. The server resolves the contact's own
 * Stripe customer from the link token; a `cus_…` supplied by the browser was a
 * capability — bind someone else's instrument to this contact — and the server
 * NEVER acts on one.
 *
 * The backend does still ACCEPT `providerCustomerId` and `provider`, marked
 * deprecated, ignored, and logged on arrival, so a cached copy of an older
 * bundle does not get a 400 midway through saving a card. That tolerance exists
 * for bundles already in the wild, NOT for this type — sending them from here
 * would keep the `public-add-card legacy-field` warnings from ever reaching
 * zero, which is the signal the backend uses to decide the fields can go.
 */
export interface CompletePublicPaymentMethodRequest {
  providerPaymentMethodId: string;
  cardholderName?: string | null;
  /** The customer re-signed the authorization a replacement link showed. */
  authorizationAccepted?: boolean;
  /** Their full name, typed as the signature on that authorization. */
  signedName?: string | null;
}

/**
 * A card as the customer who just saved it through the public add-card link
 * receives it: display fields only. No Stripe `pm_…` / `cus_…` — those never
 * leave the server on an unauthenticated route.
 */
export interface PublicSavedPaymentMethod {
  id: string;
  provider: PaymentMethodProvider | string;
  type: PaymentMethodType | string;
  brand?: string | null;
  last4?: string | null;
  expMonth?: number | null;
  expYear?: number | null;
  label?: string | null;
  cardholderName?: string | null;
  isDefault: boolean;
  isExpired: boolean;
  addedAt?: string | null;
}

// Response shape for the recurring-billing repair flow
// (`/api/public/recurring-billing-repair/:token/setup-intent`). This is a
// separate backend endpoint from the contact card-setup intent and carries a
// looser, optional Connect shape (`accountId` rather than `stripeAccountId`),
// so it intentionally does NOT extend ContactPaymentMethodSetupIntent.
export interface ContactSetupIntentResponse {
  provider: PaymentMethodProvider | string;
  clientSecret: string;
  providerCustomerId: string;
  publishableKey?: string | null;
  accountId?: string | null;
}

export interface ContactPaymentMethodRequestDetails extends PublicRepairCompanyContact {
  contactId: string;
  contactName: string;
  companyName?: string | null;
  expiresAt?: string | null;
  provider: PaymentMethodProvider | string;
  /**
   * Rails the tenant accepts. REQUIRED — the embedded component enables both
   * unless told otherwise, so an absent value must fail closed, never default
   * to enabled.
   */
  acceptCard: boolean;
  acceptACH: boolean;
  /**
   * Browser-safe Payabli embedded-component config, present only for Payabli
   * links so the public page can mount the component.
   */
  payabli?: {
    entryPoint: string | null;
    publicToken: string | null;
    mode: 'TEST' | 'LIVE';
  };
  /** Company/business IANA timezone for rendering customer-facing dates (SPEC-tz-6). */
  timezone?: string | null;
  /** Set on a card-replacement link: the card the customer is replacing. */
  replacing?: {
    /** e.g. "Visa ending in 8947". */
    description: string;
    problem: 'expired' | 'declined' | 'update';
  } | null;
  /**
   * Scheduled-charge authorizations on the card being replaced, which the
   * customer signs again for the new card.
   */
  authorizations?: Array<{ maxAmountPerCharge: number; maxTotal: number | null }>;
}

export interface CreateContactPaymentMethodDto {
  provider: PaymentMethodProvider | string;
  providerPaymentMethodId: string;
  providerCustomerId?: string;
  label?: string | null;
  cardholderName?: string | null;
  makeDefault?: boolean;
  type?: PaymentMethodType | string;
  brand?: string | null;
  last4?: string | null;
  expMonth?: number | null;
  expYear?: number | null;
}

/**
 * Payabli save-a-card: the embedded component tokenizes the card client-side
 * (temporaryToken: true) and returns a short-lived token, which the backend
 * exchanges for a permanent stored method bound to the contact.
 */
export interface TokenizeContactPaymentMethodDto {
  temporaryToken: string;
  type: PaymentMethodType | string;
  last4: string;
  brand?: string | null;
  expMonth?: number | null;
  expYear?: number | null;
  cardholderName?: string | null;
  label?: string | null;
  makeDefault?: boolean;
}

/** The public Payabli completion: a staff tokenize plus a replacement link's answer. */
export interface CompletePublicTokenizeRequest extends TokenizeContactPaymentMethodDto {
  authorizationAccepted?: boolean;
  signedName?: string | null;
}

export interface UpdateContactPaymentMethodDto {
  label?: string | null;
  makeDefault?: boolean;
}

/**
 * Which rail the secure link asks for. Omit to let the customer pick whichever
 * the business accepts — what every link did before the choice existed.
 */
export type ContactPaymentRequestRail = 'card' | 'ach';

export interface RequestContactPaymentMethodDto {
  channel: ContactPaymentRequestChannel;
  phone?: string;
  email?: string;
  rail?: ContactPaymentRequestRail;
  /** Ask the customer to replace this saved card; card links only. */
  replacesPaymentMethodId?: string;
}

/** Channels the backend accepts for a staff-recorded ACH debit authorization. */
export type StaffChargeAuthorizationChannel = 'IN_PERSON' | 'PHONE' | 'WRITTEN';

/**
 * The customer's authorization to debit their bank account, as staff record it
 * when charging a bank account in person, by phone, or from a signed/written
 * consent. Mirrors the backend's `AchAuthorizationDto`
 * (`contact-payment-method.dto.ts`) — the server stamps who took it
 * (`takenBy`) itself, so that field is never sent from the browser.
 */
export interface StaffChargeAuthorization {
  channel: StaffChargeAuthorizationChannel;
  /** The authorization wording the customer agreed to. */
  text: string;
  /** When the customer authorized it (ISO 8601). */
  acceptedAt: string;
}

export interface CollectContactPaymentDto {
  paymentMethodId: string;
  amount: number;
  invoiceId?: string;
  /** Attributes the charge to a job, once contact-verified server-side. */
  jobId?: string;
  /** Rides on top of `amount`, never surcharged. */
  tipAmount?: number;
  /** Sweeps any leftover across the contact's open invoices, oldest-first. */
  autoApply?: boolean;
  /** Opts this payment's remainder into future auto-apply too. */
  keepApplyingCredit?: boolean;
  /** The saved method backing this charge was entered for one-time use only. */
  removeAfterCharge?: boolean;
  /**
   * The method was entered by staff for this charge (the "charge a new
   * card/bank account" collector), not picked from the contact's saved
   * methods. A newly entered bank account requires `achAuthorization`.
   */
  newlyEnteredMethod?: boolean;
  /**
   * Staff started this charge by hand from the collect dialog (a saved
   * method). A staff-started bank debit requires `achAuthorization`.
   */
  staffInitiated?: boolean;
  /**
   * Required when staff charge a bank account (newly entered or saved); the
   * server refuses that charge without it.
   */
  achAuthorization?: StaffChargeAuthorization;
  communicationPreference?: 'email' | 'sms' | 'both' | 'none';
  /**
   * Overrides the `Idempotency-Key` header the route requires. Omit it and the
   * hook uses the managed per-submission key, which survives a lost response so
   * a retry replays the charge instead of running the card again. Never sent in
   * the body — the controller reads the header and 400s on a body key that
   * disagrees with it.
   */
  idempotencyKey?: string;
}

export type ManualContactPaymentMethod = 'CASH' | 'CHECK' | 'ACH' | 'OTHER';

export interface RecordContactPaymentDto {
  contactId: string;
  amount: number;
  method: ManualContactPaymentMethod;
  transactionDate?: string;
  transactionId?: string;
  methodDetails?: Record<string, unknown>;
  communicationPreference?: 'email' | 'sms' | 'both' | 'none';
  autoApply?: boolean;
  keepApplyingCredit?: boolean;
}

export interface CreateContactPaymentLinkDto {
  amount: number;
  description?: string;
}

export interface ContactPaymentRequestResult {
  requestLink: string;
  expiresAt: string;
  delivery: Record<string, { success: boolean; error?: string }>;
}

export interface ContactPaymentLinkResult {
  paymentId: string;
  requestLink: string;
  expiresAt: string;
}

export interface AccountingSync {
  status: AccountingSyncStatus;
  provider?: AccountingSyncProvider | null;
  externalId?: string | null;
  lastSynced?: string | null;
  syncVersion?: number;
  error?: string | null;
  retryCount?: number;
  /** Note id linked to QB.Customer.Notes (only one per contact). */
  qbLinkedNoteId?: string | null;
}

export interface ContactTag {
  _id: string;
  tag: string;
  color?: string;
}

export interface ContactAssignedTag {
  tagId: string;
  id?: string;
  name?: string;
  color?: string | null;
}

export interface ContactSummary {
  lifetimeRevenue: number;
  grossOpenBalance?: number;
  openBalance: number;
  /**
   * Cash the customer's bank is still debited for bank payments on the open
   * invoices, already netted out of `openBalance`; null when withheld. Cash,
   * unlike an invoice's `clearingBankTotal`, which is balance coverage.
   */
  clearingBankCash?: number | null;
  /** Provisional bank money (a contact-page collect) already inside the paid amounts; null when withheld. */
  provisionalClearingTotal?: number | null;
  paymentCreditAvailable?: number;
  creditMemoAvailable?: number;
  creditAvailable: number;
  totalJobs: number;
  lastServiceDate?: string | null;
  openInvoiceCount: number;
  hasOverdueInvoice: boolean;
  activeMembershipId?: string | null;
  membershipPlanName?: string | null;
  membershipStatus?: string | null;
  memberSavings: number;
  leadSource?: LeadSource | string | null;
}

export interface ContactSearchResult {
  id: string;
  displayName: string;
  avatarUrl?: string | null;
  primaryPhone?: string | null;
  /** The contact's other numbers — the search matches on any of them. */
  altPhone?: string | null;
  phoneNumbers?: Array<{ number: string }>;
  primaryEmail?: string | null;
  companyName?: string | null;
  contactType: ContactType | string;
  score: number;
}

/**
 * Lean parent-account reference carried on a contact read so a branch can name
 * the company it belongs to. Mirrors the operations `parentContact` projection.
 */
export interface ContactParentRef {
  id: string;
  displayName: string | null;
  companyName?: string | null;
  contactType?: ContactType | string | null;
}

export type ContactValidationWarningCode =
  | 'DUPLICATE_DISPLAY_NAME'
  | 'DUPLICATE_PRIMARY_PHONE'
  | 'TAX_EXEMPTION_EXPIRED';

export interface ContactValidationWarning {
  code: ContactValidationWarningCode;
  severity: 'warning';
  message: string;
  details?: Record<string, unknown>;
}

/**
 * NOT-138 — top-level marker the BE attaches to POST /contacts and
 * PATCH /contacts/:id responses when the contact's primary phone is
 * already used by other contacts in the same tenant. The FE renders
 * a soft warning toast or banner from this.
 */
export interface DuplicatePhoneWarning {
  code: 'DUPLICATE_PRIMARY_PHONE';
  message: string;
  phone: string;
  existingContactIds: string[];
}

export interface BillToOverrideAddress {
  street1?: string | null;
  street2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
}

export interface ContactNoteAttachment {
  id: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  downloadUrl?: string;
}

export interface ContactNote {
  id: string;
  content: string;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
  pinned: boolean;
  attachments?: ContactNoteAttachment[];
}

export interface ContactFileAttachment {
  id: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  uploadedBy?: string | null;
  createdAt: string;
  type: 'attachment' | 'photo';
}

export interface ContactPaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasMore: boolean;
}

export interface ContactCommunicationItem {
  id: string;
  eventType: string;
  title: string;
  summary?: string | null;
  channel?: ContactCommunicationChannel | string | null;
  actorType?: string | null;
  actorId?: string | null;
  occurredAt: string;
  createdAt: string;
  /** Source artifact for deep-linking into the app (e.g. 'invoice', 'estimate'). */
  sourceRecordType?: string | null;
  sourceRecordId?: string | null;
  payload?: Record<string, unknown>;
}

export interface ContactJobsSectionItem {
  id: string;
  jobNumber?: string | null;
  name?: string | null;
  status: string;
  createdAt: string;
  scheduledStart?: string | null;
  assignedTo?: string | null;
}

export interface ContactEstimatesSectionItem {
  id: string;
  estimateNumber?: string | null;
  title?: string | null;
  status: string;
  createdAt: string;
  grandTotal: number;
  salespersonId?: string | null;
}

export interface ContactInvoicesSectionItem {
  id: string;
  invoiceNumber?: string | null;
  status: string;
  /** Server-derived badge state (Clearing, Overdue...); see `InvoiceDisplayStatus`. */
  displayStatus?: InvoiceDisplayStatus;
  createdAt: string;
  dueDate?: string | null;
  total: number;
  balanceDue: number;
  amountPaid: number;
  clearingBankTotal?: number | null;
  clearingBankCash?: number | null;
  provisionalClearingTotal?: number | null;
  clearingSince?: string | null;
  jobType?: { id: string; name: string } | null;
  qbInvoiceId?: string | null;
  syncStatus: 'SYNCED' | 'NOT_SYNCED';
}

export interface ContactPaymentsSectionItem {
  id: string;
  amount: number;
  status: string;
  paymentMethod: string;
  /** How the payment came in; `CHEQUE_SCAN` for a check scanned through Payabli. */
  flow?: string | null;
  contactId?: string | null;
  invoiceId?: string | null;
  invoiceNumber?: string | null;
  processedAt?: string | null;
  createdAt: string;
  processorId?: string | null;
  gatewayProvider?: string | null;
  gatewayTxnId?: string | null;
  /** `system:webhook` once a Stripe webhook confirmed a debit in flight. */
  updatedBy?: string | null;
  transactionId?: string | null;
  /** What the user typed (check #, confirmation); never a generated or processor id. */
  reference: string | null;
  /** Set on a bank return's offsetting row: the payment the bank took back. */
  reversalOf?: string | null;
  /** Set when staff cancelled this bank payment before it cleared; it reads "Cancelled". */
  cancelledByStaffAt?: string | null;
  syncStatus: 'SYNCED' | 'NOT_SYNCED';
  qbPaymentId?: string | null;
  cardBrand?: string | null;
  cardLast4?: string | null;
  receiptId?: string | null;
  receiptNumber?: string | null;
}

export interface ContactMembershipPaymentMethodSummary {
  id: string;
  provider: PaymentMethodProvider | string;
  providerPaymentMethodId: string;
  brand?: string | null;
  last4?: string | null;
  expMonth?: number | null;
  expYear?: number | null;
  label?: string | null;
  isDefault: boolean;
}

export interface ContactMembershipBillingHistoryItem {
  id: string;
  amount: number;
  status: string;
  paymentMethod: string;
  transactionId?: string | null;
  gatewayProvider?: string | null;
  processedAt?: string | null;
  invoiceId?: string | null;
  gatewayMetadata?: Record<string, unknown> | null;
}

export interface ContactMembershipBillingHistoryResponse {
  data: ContactMembershipBillingHistoryItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasMore: boolean;
  summary: {
    totalCollected: number;
    lastPaymentAt?: string | null;
    lastPaymentAmount?: number | null;
    failedPaymentCount: number;
    nextBillingDate?: string | null;
  };
}

export interface ContactMembershipOpportunity {
  planId: string;
  planName: string;
  wouldHaveSaved: number;
  billingAmount: number | null;
  billingFrequency?: string | null;
  recentInvoiceTotal: number;
  recentInvoiceCount: number;
  since: string;
}

export interface ContactActivity {
  _id: string;
  type: string;
  description: string;
  createdAt: string;
}

/**
 * How this contact was won. Stamped at creation from the founding event and
 * immutable thereafter (`trg_contact_acquisition_immutable`), so this is
 * display data — there is no edit path and there must not appear to be one.
 *
 * All-null means the channel is not yet known. That is a real, recoverable
 * state, not a gap: the contact stays claimable by the founding-event stamp
 * when a real attribution event arrives.
 */
export interface ContactAcquisition {
  channelId: string | null;
  channelName: string | null;
  channelSlug: string | null;
  acquiredAt: string | null;
  foundingEventId: string | null;
}

export interface Contact {
  /** How many locations name this person (list rows only). */
  locationRoleCount?: number;
  /** True when this contact exists ONLY as somebody else's site contact. */
  isSiteContactOnly?: boolean;
  id: string;
  contactNumber?: string;
  firstName: string;
  lastName?: string;
  /** Role / title of the contact person (commercial). */
  title?: string | null;
  displayName?: string;
  avatarUrl?: string | null;
  primaryEmail?: string | null;
  emails?: ContactEmail[];
  primaryPhone?: string | null;
  phoneNumbers?: ContactPhoneNumber[];
  altPhone?: string | null;
  contactType: ContactType;
  companyName?: string | null;
  webAddress?: string | null;
  /**
   * QB job/sub-customer hierarchy. When set, this contact is a sub-customer
   * of the contact at parentContactId. Push translates this to QB's
   * Job=true + ParentRef. Null = top-level customer.
   */
  parentContactId?: string | null;
  /**
   * Lean parent-account snapshot hydrated by the operations read (null for
   * top-level accounts). Lets a branch render "part of Acme Corp" without a
   * second fetch.
   */
  parentContact?: ContactParentRef | null;
  /** Number of branches / linked contacts under this account (0 = none). */
  childCount?: number;
  /**
   * Legacy provenance, free-form on the server and deliberately NOT narrowed to
   * `ContactSource` here. `ContactSource` is only the vocabulary a human can
   * PICK at create time (see `CreateContactDto.source`); the stored column also
   * holds machine-written values no client can spell — `inbound-sms`,
   * `quickbooks`, … Typing the read side as the narrow union was a lie that
   * invited exactly one repair: coerce the unrecognised value to `MANUAL`. The
   * contact form did that, and the coerced value then travelled back to the API
   * as a real edit. Read it, format it, never round-trip it.
   */
  source?: string;
  /**
   * Read-only acquisition block (R2). Optional so a list response that does
   * not carry it still typechecks.
   */
  acquisition?: ContactAcquisition;
  leadSource?: LeadSource | null;
  leadSourceId?: string | null;
  language?: string | null;
  preferredContactMethod?: PreferredChannel | null;
  doNotContact?: boolean;
  smsFollowUp?: boolean;
  emailFollowUp?: boolean;
  communicationOptOut?: PreferredChannel[];
  addresses?: ContactAddress[];
  communicationPreferences?: CommunicationPreferences;
  paymentMethods?: ContactPaymentMethod[];
  membershipId?: string | null;
  activeMembershipId?: string | null;
  membershipStatus?: string | null;
  membershipPlanName?: string | null;
  membershipActivatedAt?: string | null;
  defaultPaymentMethodId?: string | null;
  membershipHistory?: string[];
  customerSavingsToDate?: number;
  customerSavingsCurrentYear?: number;
  membershipValueToDate?: number;
  utilizationScore?: number;
  accountingSync?: AccountingSync;
  // Customer-level tax exemption. Managed through the dedicated
  // `PUT /contacts/:id/tax-exemption` endpoint (not the contact create/edit
  // form). An exempt customer drives $0 tax on every document for that contact.
  isTaxExempt?: boolean;
  taxExemptCertificate?: string | null;
  taxExemptReason?: TaxExemptReason | string | null;
  taxExemptNotes?: string | null;
  taxExemptExpiry?: string | null;
  notes?: string | null;
  tags?: ContactAssignedTag[];
  tagIds?: string[];
  customFields?: Record<string, unknown>;
  isBlocked?: boolean;
  /** Soft-deleted (archived). Returned by the detail endpoint via `?includeDeleted`. */
  isDeleted?: boolean;
  assignedTo?: string | null;
  customStatus?: string | null;
  customStatusColor?: string | null;
  summary?: ContactSummary;
  validationWarnings?: ContactValidationWarning[];
  // NOT-138 — soft warning surfaced on create/update when the primary
  // phone is already used by other contacts in this tenant. Null when
  // no duplicates exist.
  duplicatePhoneWarning?: DuplicatePhoneWarning | null;
  // NOT-86 — AI-augmented fields. Always rendered with an "AI" badge so
  // end users know the source. They sit alongside deterministic fields
  // but never replace them; the backend enforces the same separation.
  aiSummary?: string | null;
  aiRiskScore?: number | null;
  aiRecommendations?: ContactAiRecommendation[];
  aiEnrichedAt?: string | null;
  aiEnrichmentVersion?: string | null;
  createdBy?: string | null;
  createdByName?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ContactAiRecommendation {
  label: string;
  action?: string;
  confidence?: number;
}

export interface ContactListParams {
  parentContactId?: string;
  page?: number;
  limit?: number;
  search?: string;
  /** CUSTOMER hides people who only exist as a location's site contact. */
  kind?: 'CUSTOMER' | 'SITE_CONTACT' | 'ALL';
  contactType?: ContactType | '';
  status?: string;
  customStatus?: string;
  source?: ContactSource | '';
  leadSource?: LeadSource | '';
  membershipStatus?: string;
  tags?: string[];
  createdFrom?: string;
  createdTo?: string;
  sortBy?:
    | 'displayName'
    | 'primaryEmail'
    | 'createdAt'
    | 'updatedAt'
    | 'contactNumber'
    | 'companyName';
  sortDir?: 'asc' | 'desc';
  [key: string]: unknown;
}

export interface ContactListResponse {
  contacts: Contact[];
  total: number;
  page: number;
  limit: number;
}

export interface CreateContactDto {
  firstName?: string;
  lastName?: string;
  title?: string | null;
  displayName?: string;
  avatarUrl?: string;
  primaryEmail?: string;
  emails?: ContactEmail[];
  primaryPhone?: string;
  phoneNumbers?: ContactPhoneNumber[];
  altPhone?: string;
  contactType?: ContactType;
  companyName?: string;
  webAddress?: string;
  parentContactId?: string | null;
  source?: ContactSource;
  leadSource?: LeadSource;
  leadSourceId?: string;
  language?: string;
  preferredContactMethod?: PreferredChannel;
  doNotContact?: boolean;
  smsFollowUp?: boolean;
  emailFollowUp?: boolean;
  communicationOptOut?: PreferredChannel[];
  addresses?: ContactAddress[];
  communicationPreferences?: CommunicationPreferences;
  membershipId?: string;
  notes?: string;
  tagIds?: string[];
  customFields?: Record<string, unknown>;
  customStatus?: string | null;
  customStatusColor?: string | null;
}

export interface UpdateContactDto extends Omit<
  Partial<CreateContactDto>,
  | 'displayName'
  | 'primaryPhone'
  | 'altPhone'
  | 'primaryEmail'
  | 'companyName'
  | 'webAddress'
  | 'leadSource'
  | 'notes'
  // `source` is system provenance — free-form on the server ('inbound-sms',
  // 'quickbooks', …), written at create time only. The server accepts and
  // ignores it on an update; there is nothing to send, because no control here
  // collects it.
  | 'source'
> {
  id: string;
  /** null clears a stale display name so the backend re-derives it from the name. */
  displayName?: string | null;
  primaryEmail?: string | null;
  primaryPhone?: string | null;
  altPhone?: string | null;
  companyName?: string | null;
  webAddress?: string | null;
  leadSource?: LeadSource | null;
  notes?: string | null;
}

export interface ImportContactsDto {
  contacts: CreateContactDto[];
}

export interface ImportContactsResponse {
  created: number;
  skipped: number;
  failed: number;
  errors?: string[];
}

/** waiting: another import runs first; paused: the system is busy. */
export type ContactImportJobStatus =
  | 'queued'
  | 'waiting'
  | 'running'
  | 'paused'
  | 'done'
  | 'failed';

/** A background CSV import, as `/contacts/import/jobs/:jobId` reports it. */
export interface ContactImportJobView {
  jobId: string;
  status: ContactImportJobStatus;
  total: number;
  processed: number;
  created: number;
  skipped: number;
  failed: number;
  /** Why it is waiting or paused, or why it stopped. */
  reason?: string;
  /** The full report, once done. */
  result?: ImportContactsResponse;
}

export interface ContactTask extends TaskSchedulingFields {
  id: string;
  contactId: string;
  /** The customer's address the task is about (where a VISIT goes). */
  locationId?: string | null;
  /** Present on reads that include the customer (task detail / task list). */
  contact?: {
    id: string;
    displayName?: string | null;
    primaryPhone?: string | null;
    primaryEmail?: string | null;
  } | null;
  title: string;
  description?: string | null;
  dueDate?: string | null;
  // `in_progress` is written as-is (UpdateTaskDto accepts it); the rest are the
  // upper-case enum the contact task form has always sent.
  status: 'OPEN' | 'in_progress' | 'COMPLETED' | 'CANCELLED';
  assignedTo?: string | null;
  createdBy: string;
  completedAt?: string | null;
  attachments?: TaskAttachment[];
  createdAt: string;
  updatedAt: string;
}

export interface AdditionalContact {
  id: string;
  name: string;
  title?: string;
  phone?: string;
  email?: string;
  isBillingContact?: boolean;
  canApprove?: boolean;
  /** True for the commercial record's first-class Contact identity. */
  isPrimary?: boolean;
  notes?: string;
}

export interface BillTo {
  id: string;
  label: string;
  contactId: string;
  isDefault?: boolean;
  overrideAddress?: BillToOverrideAddress | null;
  overrideEmail?: string;
  notes?: string;
}

export interface ContactStatus {
  id: string;
  name: string;
  color: string;
  sortOrder: number;
  isDefault: boolean;
  isClosed: boolean;
  isSystem: boolean;
  isActive: boolean;
}
